import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Pencil, Ticket, Gift, Users, Percent, DollarSign, BarChart3, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
  { key: "percent",   label: "% Off",       icon: Percent },
  { key: "fixed",     label: "$ Off",       icon: DollarSign },
  { key: "gift_card", label: "Gift Card",   icon: Gift },
  { key: "referral",  label: "Referral",    icon: Users },
];

/** Phase F.5 Batch 18 — Coupon / Promo / Gift Card / Referral admin page. */
export default function Coupons() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [redeeming, setRedeeming] = useState(null);
  const [redeemBody, setRedeemBody] = useState({ amount_applied: 0, reference_type: "manual", reference_id: "", notes: "" });
  const [filter, setFilter] = useState("all");

  const blank = { code: "", kind: "percent", amount: 0, label: "", description: "", max_uses: null, valid_from: "", valid_until: "" };

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        axios.get(`${API}/coupons/`),
        axios.get(`${API}/coupons/stats`),
      ]);
      setList(l.data?.coupons || []);
      setStats(s.data);
    } catch (e) { /* */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const body = {
        ...editing,
        amount: parseFloat(editing.amount) || 0,
        max_uses: editing.max_uses ? parseInt(editing.max_uses) : null,
        valid_from: editing.valid_from || null,
        valid_until: editing.valid_until || null,
      };
      if (editing.id) await axios.put(`${API}/coupons/${editing.id}`, body);
      else await axios.post(`${API}/coupons/`, body);
      toast.success("Saved");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Deactivate this coupon? Past redemptions stay in history.")) return;
    try { await axios.delete(`${API}/coupons/${id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error("Failed"); }
  };

  const redeem = async () => {
    try {
      await axios.post(`${API}/coupons/${redeeming.id}/redeem`, redeemBody);
      toast.success("Redeemed");
      setRedeeming(null); setRedeemBody({ amount_applied: 0, reference_type: "manual", reference_id: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Redeem failed"); }
  };

  const visible = filter === "all" ? list : list.filter((c) => c.kind === filter);

  return (
    <div className="space-y-4 p-4" data-testid="coupons-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Ticket className="h-6 w-6" /> Coupons & Promotions</h1>
          <p className="text-sm text-muted-foreground">Track promo codes, gift cards, and referral credits. Live POS integration coming soon.</p>
        </div>
        <Button onClick={() => setEditing({ ...blank })} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cp-new-btn"><Plus className="h-4 w-4 mr-1" /> New coupon</Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cp-stats">
          <Card><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Active coupons</p>
            <p className="text-2xl font-bold">{stats.active_count}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Total redemptions</p>
            <p className="text-2xl font-bold">{stats.total_redemptions}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <p className="text-xs text-muted-foreground uppercase">Value applied</p>
            <p className="text-2xl font-bold">${stats.total_value_applied.toFixed(2)}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground uppercase">Live POS</p>
              <p className="text-base font-semibold">{stats.live_pos_available ? "Available" : "Under construction"}</p>
            </div>
            <Button size="sm" variant="outline" disabled data-testid="cp-pos-cta"><Lock className="h-3.5 w-3.5 mr-1" /> Upgrade</Button>
          </CardContent></Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle>Coupons</CardTitle>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="cp-kind-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kinds</SelectItem>
                {KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {visible.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="cp-empty">No coupons yet.</p> :
            <div className="space-y-2" data-testid="cp-list">
              {visible.map((c) => {
                const Kind = (KINDS.find((k) => k.key === c.kind) || {}).icon || Ticket;
                return (
                  <div key={c.id} className="p-3 border rounded-lg flex items-center justify-between gap-2 flex-wrap" data-testid={`cp-${c.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium flex items-center gap-2 flex-wrap">
                        <Kind className="h-4 w-4" />
                        <code className="bg-muted px-2 py-0.5 rounded">{c.code}</code>
                        <Badge variant="outline">{c.kind}</Badge>
                        <span className="text-sm">{c.kind === "percent" ? `${c.amount}%` : `$${c.amount.toFixed(2)}`}</span>
                        {!c.is_active && <Badge variant="secondary">inactive</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {c.label}{c.label ? " · " : ""}Used {c.used_count}{c.max_uses ? ` / ${c.max_uses}` : ""}
                        {c.valid_from || c.valid_until ? ` · ${c.valid_from || "any"} → ${c.valid_until || "no end"}` : ""}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setRedeeming(c)} data-testid={`cp-redeem-${c.id}`} disabled={!c.is_active}>Redeem</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing({ ...c, max_uses: c.max_uses || "" })} data-testid={`cp-edit-${c.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => remove(c.id)} data-testid={`cp-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="cp-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit coupon" : "New coupon"}</DialogTitle>
            <DialogDescription>Set the code, kind, amount and validity.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Code</Label><Input value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value })} data-testid="cp-code" /></div>
              <div><Label>Kind</Label>
                <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                  <SelectTrigger data-testid="cp-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>{KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Amount</Label><Input type="number" step="0.01" value={editing.amount} onChange={(e) => setEditing({ ...editing, amount: e.target.value })} data-testid="cp-amount" /></div>
              <div><Label>Max uses (blank = unlimited)</Label><Input type="number" value={editing.max_uses || ""} onChange={(e) => setEditing({ ...editing, max_uses: e.target.value })} data-testid="cp-max-uses" /></div>
              <div><Label>Valid from</Label><Input type="date" value={editing.valid_from || ""} onChange={(e) => setEditing({ ...editing, valid_from: e.target.value })} data-testid="cp-valid-from" /></div>
              <div><Label>Valid until</Label><Input type="date" value={editing.valid_until || ""} onChange={(e) => setEditing({ ...editing, valid_until: e.target.value })} data-testid="cp-valid-until" /></div>
              <div className="col-span-2"><Label>Label / promo name</Label><Input value={editing.label || ""} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="cp-label" /></div>
              <div className="col-span-2"><Label>Description</Label><Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="cp-description" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cp-save-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!redeeming} onOpenChange={(o) => !o && setRedeeming(null)}>
        <DialogContent data-testid="cp-redeem-dialog">
          <DialogHeader>
            <DialogTitle>Redeem {redeeming?.code}</DialogTitle>
            <DialogDescription>Track a manual redemption. Live POS will auto-record here once enabled.</DialogDescription>
          </DialogHeader>
          {redeeming && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Amount applied ($)</Label><Input type="number" step="0.01" value={redeemBody.amount_applied} onChange={(e) => setRedeemBody({ ...redeemBody, amount_applied: parseFloat(e.target.value) || 0 })} data-testid="cp-redeem-amount" /></div>
              <div><Label>Reference type</Label>
                <Select value={redeemBody.reference_type} onValueChange={(v) => setRedeemBody({ ...redeemBody, reference_type: v })}>
                  <SelectTrigger data-testid="cp-redeem-ref-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="invoice">Invoice</SelectItem>
                    <SelectItem value="quote">Quote</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label>Reference ID</Label><Input value={redeemBody.reference_id} onChange={(e) => setRedeemBody({ ...redeemBody, reference_id: e.target.value })} data-testid="cp-redeem-ref-id" /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={redeemBody.notes} onChange={(e) => setRedeemBody({ ...redeemBody, notes: e.target.value })} data-testid="cp-redeem-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedeeming(null)}>Cancel</Button>
            <Button onClick={redeem} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cp-redeem-confirm-btn">Record redemption</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
